import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const MONTHS_AR = [
  'يناير','فبراير','مارس','أبريل','مايو','يونيو',
  'يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر',
];

const fmt = (n) => (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Shared stat card ──────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, color = 'text-foreground', language }) => (
  <Card className="p-5 border border-border">
    <p className="text-sm text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>{label}</p>
    <p className={`text-2xl font-bold font-mono-numeric mt-1 ${color}`}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1" lang={language === 'ar' ? 'ar' : 'en'}>{sub}</p>}
  </Card>
);

// ── Table wrapper ─────────────────────────────────────────────────────────────
const ReportTable = ({ head, rows, foot, language }) => (
  <div className="overflow-x-auto">
    <table className={`w-full ${language === 'ar' ? 'text-base' : 'text-sm'} border-collapse`}>
      <thead>
        <tr className="border-b border-border bg-muted/30">
          {head.map((h, i) => (
            <th key={i} className={`px-3 py-2 font-medium text-muted-foreground ${i === 0 ? 'text-start' : 'text-end'}`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-border/50 hover:bg-muted/20">
            {row.map((cell, j) => (
              <td key={j} className={`px-3 py-2 ${j === 0 ? 'text-start' : 'text-end font-mono-numeric'}`}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
      {foot && (
        <tfoot>
          <tr className="border-t-2 border-border bg-muted/30 font-semibold">
            {foot.map((cell, i) => (
              <td key={i} className={`px-3 py-2 ${i === 0 ? 'text-start' : 'text-end font-mono-numeric'}`}>{cell}</td>
            ))}
          </tr>
        </tfoot>
      )}
    </table>
  </div>
);

// ── Section heading ───────────────────────────────────────────────────────────
const SectionTitle = ({ children, language }) => (
  <h3 className="text-base font-semibold mb-3 mt-6 first:mt-0" lang={language === 'ar' ? 'ar' : 'en'}>{children}</h3>
);

// Reports only show data from 1 May 2025 onwards — entries before this were
// added solely to establish correct opening client/supplier balances.
const REPORT_START = new Date('2026-05-01');
const afterStart = (dateStr) => dateStr && new Date(dateStr) >= REPORT_START;

// ── Year filter ───────────────────────────────────────────────────────────────
const YearFilter = ({ value, onChange, t }) => {
  const years = [2024, 2025, 2026];
  return (
    <select
      value={value}
      onChange={e => onChange(Number(e.target.value))}
      className="border border-border rounded-md px-3 py-1.5 text-sm bg-background"
    >
      {years.map(y => <option key={y} value={y}>{y}</option>)}
    </select>
  );
};

// ── Month + Year filter ───────────────────────────────────────────────────────
const MonthYearFilter = ({ month, year, onMonth, onYear, language, allowFullYear = false }) => {
  const months = language === 'ar' ? MONTHS_AR : MONTHS;
  return (
    <div className="flex gap-2">
      <select value={month} onChange={e => onMonth(Number(e.target.value))}
        className="border border-border rounded-md px-3 py-1.5 text-sm bg-background">
        {allowFullYear && <option value={0}>{language === 'ar' ? 'السنة كاملة' : 'Full Year'}</option>}
        {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
      </select>
      <select value={year} onChange={e => onYear(Number(e.target.value))}
        className="border border-border rounded-md px-3 py-1.5 text-sm bg-background">
        {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// REPORT 1 — REVENUE
// ══════════════════════════════════════════════════════════════════════════════
const RevenueReport = ({ invoices, payments, t, language }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const cur = t('currency');
  const months = language === 'ar' ? MONTHS_AR : MONTHS;

  const filteredInvoices = useMemo(() =>
    invoices.filter(inv => {
      const d = inv.date ? new Date(inv.date) : null;
      return d && d.getFullYear() === year && afterStart(inv.date);
    }), [invoices, year]);

  // By month
  const byMonth = useMemo(() => {
    const map = {};
    filteredInvoices.forEach(inv => {
      const mo = new Date(inv.date).getMonth();
      if (!map[mo]) map[mo] = { invoiced: 0 };
      map[mo].invoiced += inv.subtotal || 0;
    });
    return map;
  }, [filteredInvoices]);

  // Per-client ratio of subtotal/total to strip VAT from payments
  const clientNetRatio = useMemo(() => {
    const m = {};
    invoices.forEach(inv => {
      const n = inv.client_name;
      if (!m[n]) m[n] = { subtotal: 0, total: 0 };
      m[n].subtotal += inv.subtotal || 0;
      m[n].total    += inv.total    || 0;
    });
    return Object.fromEntries(
      Object.entries(m).map(([n, d]) => [n, d.total > 0 ? d.subtotal / d.total : 1 / 1.14])
    );
  }, [invoices]);

  // Collected per month from payments — converted to net (VAT stripped)
  const collectedByMonth = useMemo(() => {
    const map = {};
    payments.filter(p => p.entity_type === 'client' && afterStart(p.date)).forEach(p => {
      const d = new Date(p.date);
      if (d.getFullYear() !== year) return;
      const mo = d.getMonth();
      const ratio = clientNetRatio[p.entity_id] ?? (1 / 1.14);
      map[mo] = (map[mo] || 0) + (p.amount || 0) * ratio;
    });
    return map;
  }, [payments, year, clientNetRatio]);

  const monthRows = months.map((name, i) => {
    const inv = byMonth[i]?.invoiced || 0;
    const coll = collectedByMonth[i] || 0;
    const out = inv - coll;
    return [name, `${fmt(inv)} ${cur}`, `${fmt(coll)} ${cur}`, `${fmt(out)} ${cur}`];
  }).filter((_, i) => (byMonth[i]?.invoiced || 0) > 0 || (collectedByMonth[i] || 0) > 0);

  // By client — all-time net payments (no afterStart: pre-May downpayments reduce outstanding)
  const paymentsByClient = useMemo(() => {
    const m = {};
    payments.filter(p => p.entity_type === 'client').forEach(p => {
      const ratio = clientNetRatio[p.entity_id] ?? (1 / 1.14);
      m[p.entity_id] = (m[p.entity_id] || 0) + (p.amount || 0) * ratio;
    });
    return m;
  }, [payments, clientNetRatio]);

  const byClient = useMemo(() => {
    const map = {};
    filteredInvoices.forEach(inv => {
      const name = inv.client_name;
      if (!map[name]) map[name] = { count: 0, invoiced: 0 };
      map[name].count += 1;
      map[name].invoiced += inv.subtotal || 0;
    });
    return map;
  }, [filteredInvoices]);

  const clientRows = Object.entries(byClient)
    .sort((a, b) => b[1].invoiced - a[1].invoiced)
    .map(([name, d]) => {
      const paid = paymentsByClient[name] || 0;
      const out = d.invoiced - paid;
      return [name, d.count, `${fmt(d.invoiced)} ${cur}`, `${fmt(paid)} ${cur}`, `${fmt(Math.max(0, out))} ${cur}`];
    });

  const totalInv = filteredInvoices.reduce((s, i) => s + (i.subtotal || 0), 0);
  const totalColl = Object.values(collectedByMonth).reduce((s, v) => s + v, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <YearFilter value={year} onChange={setYear} t={t} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label={t('totalInvoiced')} value={`${fmt(totalInv)} ${cur}`} language={language} />
        <StatCard label={t('totalCollected')} value={`${fmt(totalColl)} ${cur}`} color="text-green-600" language={language} />
        <StatCard label={t('outstanding')} value={`${fmt(Math.max(0, totalInv - totalColl))} ${cur}`} color="text-red-600" language={language} />
      </div>
      <SectionTitle language={language}>{t('revenueReport')} — {year}</SectionTitle>
      {monthRows.length > 0 ? (
        <ReportTable
          head={[language === 'ar' ? 'الشهر' : 'Month', t('totalInvoiced'), t('totalCollected'), t('outstanding')]}
          rows={monthRows}
          foot={[language === 'ar' ? 'الإجمالي' : 'Total', `${fmt(totalInv)} ${cur}`, `${fmt(totalColl)} ${cur}`, `${fmt(Math.max(0, totalInv - totalColl))} ${cur}`]}
        />
      ) : <p className="text-sm text-muted-foreground py-4">{t('noDataForPeriod')}</p>}
      <SectionTitle language={language}>{language === 'ar' ? 'تفصيل العملاء' : 'By Client'}</SectionTitle>
      {clientRows.length > 0 ? (
        <ReportTable
          head={[t('clients'), '#', t('totalInvoiced'), t('totalCollected'), t('outstanding')]}
          rows={clientRows}
        />
      ) : <p className="text-sm text-muted-foreground py-4">{t('noDataForPeriod')}</p>}
    </div>
  );
};

const ClientBalancesReport = ({ invoices, payments, t, language }) => {
  const cur = t('currency');

  const data = useMemo(() => {
    const map = {};
    invoices.forEach(inv => {
      const n = inv.client_name;
      if (!map[n]) map[n] = { count: 0, invoiced: 0 };
      map[n].count++;
      map[n].invoiced += inv.total || 0;
    });
    const paid = {};
    payments.filter(p => p.entity_type === 'client').forEach(p => {
      paid[p.entity_id] = (paid[p.entity_id] || 0) + (p.amount || 0);
    });
    return Object.entries(map)
      .map(([name, d]) => ({ name, ...d, paid: paid[name] || 0, balance: d.invoiced - (paid[name] || 0) }))
      .sort((a, b) => b.balance - a.balance);
  }, [invoices, payments]);

  const totalInv  = data.reduce((s, d) => s + d.invoiced, 0);
  const totalPaid = data.reduce((s, d) => s + d.paid, 0);
  const totalBal  = data.reduce((s, d) => s + d.balance, 0);

  const rows = data.map(d => {
    const balColor = d.balance <= 0 ? 'text-green-600' : d.paid > 0 ? 'text-yellow-600' : 'text-red-600';
    return [
      d.name,
      d.count,
      `${fmt(d.invoiced)} ${cur}`,
      `${fmt(d.paid)} ${cur}`,
      <span className={`font-semibold ${balColor}`}>{fmt(d.balance)} {cur}</span>,
    ];
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label={t('totalInvoiced')} value={`${fmt(totalInv)} ${cur}`} language={language} />
        <StatCard label={t('totalCollected')} value={`${fmt(totalPaid)} ${cur}`} color="text-green-600" language={language} />
        <StatCard label={t('outstanding')} value={`${fmt(Math.max(0, totalBal))} ${cur}`} color="text-red-600" language={language} />
      </div>
      <ReportTable
        head={[t('clients'), '#', t('totalInvoiced'), t('totalCollected'), t('outstanding')]}
        rows={rows}
        foot={[language === 'ar' ? 'الإجمالي' : 'Total', data.length, `${fmt(totalInv)} ${cur}`, `${fmt(totalPaid)} ${cur}`, `${fmt(Math.max(0, totalBal))} ${cur}`]}
      />
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// REPORT 3 — P&L SUMMARY
// ══════════════════════════════════════════════════════════════════════════════
const LEDGER_EXCLUDE = ['Client Payment', 'Supplier Payment', 'Outsourcing', 'Salaries', 'Withdrawal'];

const PLReport = ({ invoices, employees, outsourcingPayments, ledger, etaReceived = [], snapshotCosts = {}, t, language }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const cur = t('currency');
  const months = language === 'ar' ? MONTHS_AR : MONTHS;

  const liveMonthlyPayroll = useMemo(() =>
    employees.reduce((s, e) => s + (e.salary || 0) - (e.ss || 0), 0), [employees]);

  const rows = useMemo(() => {
    return Array.from({ length: 12 }, (_, mo) => {
      const moStr = String(mo + 1).padStart(2, '0');
      if (!afterStart(`${year}-${moStr}-01`)) return null;
      const snapshotKey = `${year}-${moStr}`;
      const monthlyPayroll = snapshotCosts[snapshotKey] !== undefined ? snapshotCosts[snapshotKey] : liveMonthlyPayroll;
      const rev = invoices
        .filter(i => i.date && afterStart(i.date) && new Date(i.date).getFullYear() === year && new Date(i.date).getMonth() === mo)
        .reduce((s, i) => s + (i.subtotal || 0), 0);
      const outCost = outsourcingPayments
        .filter(p => p.payment_date && afterStart(p.payment_date) && new Date(p.payment_date).getFullYear() === year && new Date(p.payment_date).getMonth() === mo)
        .reduce((s, p) => s + (p.amount || 0), 0);
      const supplPay = etaReceived
        .filter(inv => inv.date_issued && afterStart(inv.date_issued) && new Date(inv.date_issued).getFullYear() === year && new Date(inv.date_issued).getMonth() === mo)
        .reduce((s, inv) => s + (inv.net_amount || 0), 0);
      const otherExp = ledger
        .filter(e => e.credit > 0 && e.date && afterStart(e.date) && new Date(e.date).getFullYear() === year && new Date(e.date).getMonth() === mo && !LEDGER_EXCLUDE.includes(e.transaction_type))
        .reduce((s, e) => s + (e.credit || 0), 0);
      if (rev === 0 && outCost === 0 && supplPay === 0 && otherExp === 0) return null;
      const totalCosts = monthlyPayroll + outCost + supplPay + otherExp;
      const net = rev - totalCosts;
      return { mo, rev, monthlyPayroll, outCost, supplPay, otherExp, totalCosts, net };
    }).filter(Boolean);
  }, [invoices, etaReceived, employees, outsourcingPayments, ledger, year, liveMonthlyPayroll, snapshotCosts]);

  const totals = rows.reduce((acc, r) => ({
    rev: acc.rev + r.rev,
    payroll: acc.payroll + r.monthlyPayroll,
    out: acc.out + r.outCost,
    supp: acc.supp + r.supplPay,
    other: acc.other + r.otherExp,
    costs: acc.costs + r.totalCosts,
    net: acc.net + r.net,
  }), { rev: 0, payroll: 0, out: 0, supp: 0, other: 0, costs: 0, net: 0 });

  const tableRows = rows.map(r => [
    months[r.mo],
    `${fmt(r.rev)} ${cur}`,
    `${fmt(r.monthlyPayroll)} ${cur}`,
    `${fmt(r.outCost)} ${cur}`,
    `${fmt(r.supplPay)} ${cur}`,
    `${fmt(r.otherExp)} ${cur}`,
    `${fmt(r.totalCosts)} ${cur}`,
    <span className={r.net >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>{fmt(r.net)} {cur}</span>,
  ]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <YearFilter value={year} onChange={setYear} t={t} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label={t('revenue')} value={`${fmt(totals.rev)} ${cur}`} color="text-green-600" language={language} />
        <StatCard label={t('payrollCost')} value={`${fmt(totals.payroll)} ${cur}`} color="text-red-600" language={language} />
        <StatCard label={t('outsourcingCost')} value={`${fmt(totals.out)} ${cur}`} color="text-red-600" language={language} />
        <StatCard label={language === 'ar' ? 'مصاريف أخرى' : 'Other Expenses'} value={`${fmt(totals.other)} ${cur}`} color="text-red-600" language={language} />
        <StatCard label={t('netProfit')} value={`${fmt(totals.net)} ${cur}`} color={totals.net >= 0 ? 'text-green-600' : 'text-red-600'} language={language} />
      </div>
      {tableRows.length > 0 ? (
        <ReportTable
          head={[language === 'ar' ? 'الشهر' : 'Month', t('revenue'), t('payrollCost'), t('outsourcingCost'), language === 'ar' ? 'فواتير الموردين' : 'Supplier Invoices', language === 'ar' ? 'مصاريف أخرى' : 'Other Expenses', t('totalCosts'), t('netProfit')]}
          rows={tableRows}
          foot={[language === 'ar' ? 'الإجمالي' : 'Total', `${fmt(totals.rev)} ${cur}`, `${fmt(totals.payroll)} ${cur}`, `${fmt(totals.out)} ${cur}`, `${fmt(totals.supp)} ${cur}`, `${fmt(totals.other)} ${cur}`, `${fmt(totals.costs)} ${cur}`, `${fmt(totals.net)} ${cur}`]}
        />
      ) : <p className="text-sm text-muted-foreground py-4">{t('noDataForPeriod')}</p>}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// REPORT 4 — MONTHLY INCOME STATEMENT
// ══════════════════════════════════════════════════════════════════════════════
const IncomeStatement = ({ invoices, payments, employees, outsourcingPayments, ledger, t, language }) => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear]   = useState(now.getFullYear());
  const cur = t('currency');

  const data = useMemo(() => {
    const inMonth = (dateStr) => {
      if (!dateStr) return false;
      const d = new Date(dateStr);
      return d.getFullYear() === year && d.getMonth() + 1 === month;
    };
    const monthInvoices = invoices.filter(i => inMonth(i.date));
    const sales     = monthInvoices.reduce((s, i) => s + (i.subtotal || 0), 0);
    const vat       = monthInvoices.reduce((s, i) => s + (i.vat_amount || 0), 0);
    const totalRev  = sales + vat;
    const outCost   = outsourcingPayments.filter(p => inMonth(p.payment_date)).reduce((s, p) => s + (p.amount || 0), 0);
    const grossProfit = totalRev - outCost;
    const payroll   = employees.reduce((s, e) => s + (e.salary || 0) + (e.ss || 0), 0);
    const supplPay  = payments.filter(p => p.entity_type === 'supplier' && inMonth(p.date)).reduce((s, p) => s + (p.amount || 0), 0);
    const otherExp  = ledger
      .filter(e => e.credit > 0 && inMonth(e.date) && !LEDGER_EXCLUDE.includes(e.transaction_type))
      .reduce((s, e) => s + (e.credit || 0), 0);
    const opEx      = payroll + supplPay + otherExp;
    const netIncome = grossProfit - opEx;
    return { sales, vat, totalRev, outCost, grossProfit, payroll, supplPay, otherExp, opEx, netIncome };
  }, [invoices, payments, employees, outsourcingPayments, ledger, month, year]);

  const Row = ({ label, value, indent = false, bold = false, separator = false, color }) => (
    <>
      {separator && <div className="border-t border-border my-1" />}
      <div className={`flex justify-between items-center py-1.5 ${indent ? 'ps-6' : ''}`}>
        <span className={`text-sm ${bold ? 'font-semibold' : 'text-muted-foreground'}`} lang={language === 'ar' ? 'ar' : 'en'}>{label}</span>
        <span className={`text-sm font-mono-numeric ${bold ? 'font-semibold' : ''} ${color || ''}`}>{fmt(value)} {cur}</span>
      </div>
    </>
  );

  const months = language === 'ar' ? MONTHS_AR : MONTHS;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <MonthYearFilter month={month} year={year} onMonth={setMonth} onYear={setYear} language={language} />
      </div>
      <Card className="p-6 max-w-xl border border-border">
        <h3 className="text-base font-bold mb-4 text-center" lang={language === 'ar' ? 'ar' : 'en'}>
          {t('incomeStatement')} — {months[month - 1]} {year}
        </h3>
        <div className="space-y-0.5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1" lang={language === 'ar' ? 'ar' : 'en'}>{t('revenue')}</p>
          <Row label={language === 'ar' ? 'المبيعات (صافي)' : 'Sales (net)'} value={data.sales} indent />
          <Row label={t('vatCollected')} value={data.vat} indent />
          <Row label={language === 'ar' ? 'إجمالي الإيرادات' : 'Total Revenue'} value={data.totalRev} bold separator color="text-green-700" />

          <div className="mt-3" />
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1" lang={language === 'ar' ? 'ar' : 'en'}>{t('costOfSales')}</p>
          <Row label={t('outsourcingCost')} value={data.outCost} indent />
          <Row label={t('grossProfit')} value={data.grossProfit} bold separator color={data.grossProfit >= 0 ? 'text-green-700' : 'text-red-600'} />

          <div className="mt-3" />
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1" lang={language === 'ar' ? 'ar' : 'en'}>{t('operatingExpenses')}</p>
          <Row label={t('payrollCost')} value={data.payroll} indent />
          <Row label={t('supplierPayments')} value={data.supplPay} indent />
          <Row label={language === 'ar' ? 'مصاريف أخرى' : 'Other Expenses'} value={data.otherExp} indent />
          <Row label={t('operatingExpenses')} value={data.opEx} bold separator />

          <div className="mt-2 border-t-2 border-border pt-2">
            <div className="flex justify-between items-center py-1.5">
              <span className="text-base font-bold" lang={language === 'ar' ? 'ar' : 'en'}>{t('netIncome')}</span>
              <span className={`text-base font-bold font-mono-numeric ${data.netIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {fmt(data.netIncome)} {cur}
              </span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// REPORT 5 — ORDERS REPORT
// ══════════════════════════════════════════════════════════════════════════════
const OrdersReport = ({ orders, t, language }) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [itemFilter,   setItemFilter]   = useState('all');

  const clients   = useMemo(() => [...new Set(orders.map(o => o.client_name))].sort(), [orders]);
  const itemCodes = useMemo(() => [...new Set(orders.map(o => o.item_code))].sort(), [orders]);

  const filtered = useMemo(() => orders.filter(o =>
    (statusFilter === 'all' || o.status === statusFilter) &&
    (clientFilter === 'all' || o.client_name === clientFilter) &&
    (itemFilter   === 'all' || o.item_code   === itemFilter)
  ), [orders, statusFilter, clientFilter, itemFilter]);

  const counts = useMemo(() => ({
    total:        orders.length,
    pending:      orders.filter(o => o.status === 'pending').length,
    inProduction: orders.filter(o => o.status === 'in-production').length,
    completed:    orders.filter(o => o.status === 'completed').length,
  }), [orders]);

  const statusBadge = (s) => {
    const map = {
      pending:       'bg-yellow-100 text-yellow-800',
      'in-production':'bg-blue-100 text-blue-800',
      completed:     'bg-green-100 text-green-800',
      cancelled:     'bg-red-100 text-red-800',
    };
    const labels = {
      pending:        t('pending'),
      'in-production': t('inProduction'),
      completed:      t('completed'),
    };
    return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${map[s] || 'bg-muted text-muted-foreground'}`} lang={language === 'ar' ? 'ar' : 'en'}>{labels[s] || s}</span>;
  };

  const rows = filtered.map(o => {
    const pieces = (o.variants || []).reduce((s, v) => s + (v.quantity || 0), 0);
    return [
      o.order_number,
      o.client_name,
      o.items,
      pieces.toLocaleString(),
      statusBadge(o.status),
      o.procurement_date || '—',
      o.production_date  || '—',
      o.delivery_date    || '—',
    ];
  });

  const selClass = "border border-border rounded-md px-3 py-1.5 text-sm bg-background";

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label={language === 'ar' ? 'إجمالي الطلبات' : 'Total Orders'} value={counts.total} language={language} />
        <StatCard label={t('pending')} value={counts.pending} color="text-yellow-600" language={language} />
        <StatCard label={t('inProduction')} value={counts.inProduction} color="text-blue-600" language={language} />
        <StatCard label={t('completed')} value={counts.completed} color="text-green-600" language={language} />
      </div>
      <div className="flex flex-wrap gap-2">
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={selClass}>
          <option value="all">{t('all')}</option>
          <option value="pending">{t('pending')}</option>
          <option value="in-production">{t('inProduction')}</option>
          <option value="completed">{t('completed')}</option>
        </select>
        <select value={clientFilter} onChange={e => setClientFilter(e.target.value)} className={selClass}>
          <option value="all">{language === 'ar' ? 'كل العملاء' : 'All Clients'}</option>
          {clients.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={itemFilter} onChange={e => setItemFilter(e.target.value)} className={selClass}>
          <option value="all">{language === 'ar' ? 'كل الأصناف' : 'All Items'}</option>
          {itemCodes.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <ReportTable
        head={[t('orderNumber'), t('clients'), language === 'ar' ? 'الصنف' : 'Item', t('totalPieces'), t('status'), language === 'ar' ? 'الشراء' : 'Procurement', language === 'ar' ? 'الإنتاج' : 'Production', language === 'ar' ? 'التسليم' : 'Delivery']}
        rows={rows}
      />
      {rows.length === 0 && <p className="text-sm text-muted-foreground py-4">{t('noDataForPeriod')}</p>}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// REPORT 6 — DELIVERY PERFORMANCE
// ══════════════════════════════════════════════════════════════════════════════
const DeliveryPerformanceReport = ({ orders, t, language }) => {
  const daysBetween = (a, b) => {
    if (!a || !b) return null;
    return Math.round((new Date(b) - new Date(a)) / 86400000);
  };

  const completed = useMemo(() =>
    orders.filter(o => o.status === 'fulfilled' && o.procurement_date && o.delivery_date)
      .map(o => ({
        ...o,
        procToProd: daysBetween(o.procurement_date, o.production_date),
        prodToDel:  daysBetween(o.production_date, o.delivery_date),
        total:      daysBetween(o.procurement_date, o.delivery_date),
      })), [orders]);

  const avg = (key) => {
    const vals = completed.map(o => o[key]).filter(v => v !== null);
    return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : 0;
  };

  const rows = completed.map(o => [
    o.order_number,
    o.client_name,
    o.items,
    o.procToProd !== null ? o.procToProd : '—',
    o.prodToDel  !== null ? o.prodToDel  : '—',
    <span className="font-semibold">{o.total !== null ? o.total : '—'}</span>,
  ]);

  const avgRow = [
    t('averages'), '', '',
    Math.round(avg('procToProd')),
    Math.round(avg('prodToDel')),
    <span className="font-semibold">{Math.round(avg('total'))}</span>,
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label={language === 'ar' ? 'عدد الطلبات المكتملة' : 'Completed Orders'} value={completed.length} language={language} />
        <StatCard label={t('avgCycleTime')} value={`${Math.round(avg('total'))} ${language === 'ar' ? 'يوم' : 'days'}`} language={language} />
        <StatCard label={language === 'ar' ? 'متوسط إنتاج → تسليم' : 'Avg Prod → Delivery'} value={`${Math.round(avg('prodToDel'))} ${language === 'ar' ? 'يوم' : 'days'}`} language={language} />
      </div>
      {rows.length > 0 ? (
        <ReportTable
          head={[t('orderNumber'), t('clients'), language === 'ar' ? 'الصنف' : 'Item', t('procToProduction'), t('prodToDelivery'), t('totalCycle')]}
          rows={rows}
          foot={avgRow}
        />
      ) : <p className="text-sm text-muted-foreground py-4">{t('noDataForPeriod')}</p>}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// REPORT 7 — PAYROLL SUMMARY
// ══════════════════════════════════════════════════════════════════════════════
const PayrollSummaryReport = ({ employees, t, language }) => {
  const cur = t('currency');

  const byCategory = useMemo(() => {
    const map = {};
    employees.forEach(e => {
      const cat = e.category || 'Other';
      if (!map[cat]) map[cat] = { count: 0, salary: 0, ss: 0 };
      map[cat].count++;
      map[cat].salary += e.salary || 0;
      map[cat].ss     += e.ss     || 0;
    });
    return map;
  }, [employees]);

  const totals = useMemo(() => ({
    salary: employees.reduce((s, e) => s + (e.salary || 0), 0),
    ss:     employees.reduce((s, e) => s + (e.ss || 0), 0),
    net:    employees.reduce((s, e) => s + (e.salary || 0) + (e.ss || 0), 0),
  }), [employees]);

  const catRows = Object.entries(byCategory)
    .sort((a, b) => b[1].salary - a[1].salary)
    .map(([cat, d]) => [
      cat,
      d.count,
      `${fmt(d.salary)} ${cur}`,
      `${fmt(d.ss)} ${cur}`,
      `${fmt(d.salary + d.ss)} ${cur}`,
    ]);

  const empRows = [...employees]
    .sort((a, b) => (b.salary || 0) - (a.salary || 0))
    .map(e => [
      e.name,
      e.employee_number,
      e.category,
      `${fmt(e.salary)} ${cur}`,
      `${fmt(e.ss)} ${cur}`,
      `${fmt((e.salary || 0) + (e.ss || 0))} ${cur}`,
    ]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label={t('employees')} value={employees.length} language={language} />
        <StatCard label={t('baseSalary')} value={`${fmt(totals.salary)} ${cur}`} language={language} />
        <StatCard label={language === 'ar' ? 'التأمينات الاجتماعية' : 'Social Security'} value={`${fmt(totals.ss)} ${cur}`} language={language} />
        <StatCard label={t('totalNetPay')} value={`${fmt(totals.net)} ${cur}`} color="text-primary" language={language} />
      </div>
      <SectionTitle language={language}>{language === 'ar' ? 'تفصيل حسب الفئة' : 'By Category'}</SectionTitle>
      <ReportTable
        head={[t('category'), t('employees'), t('baseSalary'), language === 'ar' ? 'تأمينات' : 'SS', t('totalNetPay')]}
        rows={catRows}
        foot={[language === 'ar' ? 'الإجمالي' : 'Total', employees.length, `${fmt(totals.salary)} ${cur}`, `${fmt(totals.ss)} ${cur}`, `${fmt(totals.net)} ${cur}`]}
      />
      <SectionTitle language={language}>{language === 'ar' ? 'جميع الموظفين' : 'All Employees'}</SectionTitle>
      <ReportTable
        head={[t('name'), language === 'ar' ? 'الرقم الوظيفي' : 'Emp #', t('category'), t('salary'), language === 'ar' ? 'تأمينات' : 'SS', t('netPay')]}
        rows={empRows}
      />
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// REPORT 8 — OUTSOURCING COST REPORT
// ══════════════════════════════════════════════════════════════════════════════
const OutsourcingCostReport = ({ workers, jobs, outsourcingPayments, t, language }) => {
  const cur = t('currency');

  const workerMap = useMemo(() => {
    const m = {};
    workers.forEach(w => { m[w.id] = w.name; });
    return m;
  }, [workers]);

  const byWorker = useMemo(() => {
    const m = {};
    workers.forEach(w => { m[w.id] = { name: w.name, jobs: 0, completed: 0, work: 0 }; });
    jobs.forEach(j => {
      if (!m[j.worker_id]) return;
      m[j.worker_id].jobs++;
      if (j.status === 'completed') m[j.worker_id].completed++;
      m[j.worker_id].work += j.total_cost || 0;
    });
    outsourcingPayments.forEach(p => {
      if (!m[p.worker_id]) m[p.worker_id] = { name: p.worker_name, jobs: 0, completed: 0, work: 0, paid: 0 };
      if (!m[p.worker_id].paid) m[p.worker_id].paid = 0;
      m[p.worker_id].paid += p.amount || 0;
    });
    return Object.entries(m).map(([id, d]) => ({
      ...d,
      paid:    d.paid || 0,
      balance: (d.work || 0) - (d.paid || 0),
    }));
  }, [workers, jobs, outsourcingPayments]);

  const byItem = useMemo(() => {
    const m = {};
    jobs.forEach(j => {
      const code = j.item_code || '—';
      if (!m[code]) m[code] = { jobs: 0, qty: 0, cost: 0 };
      m[code].jobs++;
      m[code].qty  += j.quantity   || 0;
      m[code].cost += j.total_cost || 0;
    });
    return Object.entries(m).sort((a, b) => b[1].cost - a[1].cost);
  }, [jobs]);

  const totals = useMemo(() => ({
    jobs:    jobs.length,
    work:    byWorker.reduce((s, d) => s + d.work,    0),
    paid:    byWorker.reduce((s, d) => s + d.paid,    0),
    balance: byWorker.reduce((s, d) => s + d.balance, 0),
  }), [jobs, byWorker]);

  const workerRows = byWorker.map(d => [
    d.name,
    d.jobs,
    d.completed,
    `${fmt(d.work)} ${cur}`,
    `${fmt(d.paid)} ${cur}`,
    <span className={`font-semibold ${d.balance <= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(d.balance)} {cur}</span>,
  ]);

  const itemRows = byItem.map(([code, d]) => [
    code,
    d.jobs,
    d.qty.toLocaleString(),
    `${fmt(d.cost)} ${cur}`,
    d.jobs > 0 ? `${fmt(d.cost / d.jobs)} ${cur}` : '—',
  ]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label={language === 'ar' ? 'إجمالي الأعمال' : 'Total Jobs'} value={totals.jobs} language={language} />
        <StatCard label={language === 'ar' ? 'إجمالي قيمة العمل' : 'Total Work Value'} value={`${fmt(totals.work)} ${cur}`} language={language} />
        <StatCard label={t('totalCollected')} value={`${fmt(totals.paid)} ${cur}`} color="text-green-600" language={language} />
        <StatCard label={t('outstanding')} value={`${fmt(Math.max(0, totals.balance))} ${cur}`} color={totals.balance > 0 ? 'text-red-600' : 'text-green-600'} language={language} />
      </div>
      <SectionTitle language={language}>{language === 'ar' ? 'تفصيل حسب العامل' : 'By Worker'}</SectionTitle>
      <ReportTable
        head={[t('name'), language === 'ar' ? 'الأعمال' : 'Jobs', language === 'ar' ? 'مكتمل' : 'Done', language === 'ar' ? 'قيمة العمل' : 'Work Value', t('totalCollected'), t('outstanding')]}
        rows={workerRows}
      />
      <SectionTitle language={language}>{language === 'ar' ? 'تفصيل حسب كود الصنف' : 'By Item Code'}</SectionTitle>
      <ReportTable
        head={[t('itemCode'), language === 'ar' ? 'الأعمال' : 'Jobs', t('quantity'), language === 'ar' ? 'إجمالي التكلفة' : 'Total Cost', language === 'ar' ? 'متوسط التكلفة/عمل' : 'Avg Cost/Job']}
        rows={itemRows}
      />
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// REPORT — VAT REPORT (detailed monthly)
// ══════════════════════════════════════════════════════════════════════════════
const VATReport = ({ getAuthHeader, t, language }) => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear]   = useState(now.getFullYear());
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);
  const cur = t('currency');
  const isAr = language === 'ar';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/vat-report/monthly`, {
        headers: getAuthHeader(),
        params: { year, month },
      });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [year, month, getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const months = language === 'ar' ? MONTHS_AR : MONTHS;
  const s = data?.summary;

  const fmtDate = (d) => {
    if (!d) return '—';
    const [y, m, day] = d.split('-');
    return `${day}-${m}-${y}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <MonthYearFilter month={month} year={year} onMonth={setMonth} onYear={setYear} language={language} />
      </div>

      {loading && <p className="text-sm text-muted-foreground py-4">{t('loading')}</p>}

      {!loading && s && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard label={isAr ? 'صافي المبيعات' : 'Sales Net'} value={`${fmt(s.output_net)} ${cur}`} language={language} />
            <StatCard label={isAr ? 'ضريبة المبيعات' : 'Output VAT'} value={`${fmt(s.output_vat)} ${cur}`} color="text-blue-600" language={language} />
            <StatCard label={isAr ? 'صافي المشتريات' : 'Purchases Net'} value={`${fmt(s.input_net)} ${cur}`} language={language} />
            <StatCard label={isAr ? 'ضريبة المشتريات' : 'Input VAT'} value={`${fmt(s.input_vat)} ${cur}`} color="text-orange-600" language={language} />
            <StatCard
              label={isAr ? 'صافي الضريبة' : 'Net VAT'}
              value={`${s.net_vat >= 0 ? '+' : ''}${fmt(s.net_vat)} ${cur}`}
              color={s.net_vat >= 0 ? 'text-red-600' : 'text-green-600'}
              sub={s.net_vat >= 0 ? (isAr ? 'مستحق للضريبة' : 'Owed to tax authority') : (isAr ? 'رصيد ضريبي' : 'Tax credit')}
              language={language}
            />
          </div>

          {/* Side-by-side invoice tables */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Invoices issued (sales) */}
            <div>
              <SectionTitle language={language}>
                {isAr ? 'الفواتير الصادرة' : 'Invoices Issued'} ({months[month - 1]} {year})
              </SectionTitle>
              {data.sales.length > 0 ? (
                <ReportTable
                  head={[isAr ? 'رقم الفاتورة' : 'Invoice #', isAr ? 'التاريخ' : 'Date', isAr ? 'العميل' : 'Client', isAr ? 'صافي' : 'Net', isAr ? 'ضريبة' : 'VAT']}
                  rows={data.sales.map(i => [
                    i.invoice_number,
                    fmtDate(i.date),
                    i.client_name,
                    `${fmt(i.net_amount)} ${cur}`,
                    i.vat_amount > 0 ? `${fmt(i.vat_amount)} ${cur}` : '—',
                  ])}
                  foot={[isAr ? 'الإجمالي' : 'Total', '', `${data.sales.length} ${isAr ? 'فاتورة' : 'invoices'}`, `${fmt(s.output_net)} ${cur}`, `${fmt(s.output_vat)} ${cur}`]}
                  language={language}
                />
              ) : (
                <p className="text-sm text-muted-foreground py-4">{t('noDataForPeriod')}</p>
              )}
            </div>

            {/* Invoices received (ETA purchases) */}
            <div>
              <SectionTitle language={language}>
                {isAr ? 'الفواتير الواردة (ETA)' : 'Invoices Received (ETA)'} ({months[month - 1]} {year})
              </SectionTitle>
              {data.purchases.length > 0 ? (
                <ReportTable
                  head={[isAr ? 'رقم الفاتورة' : 'Invoice #', isAr ? 'التاريخ' : 'Date', isAr ? 'المورد' : 'Supplier', isAr ? 'صافي' : 'Net', isAr ? 'ضريبة' : 'VAT']}
                  rows={data.purchases.map(p => [
                    p.invoice_number || '—',
                    fmtDate(p.date),
                    p.supplier_name,
                    `${fmt(p.net_amount)} ${cur}`,
                    p.vat_amount > 0 ? `${fmt(p.vat_amount)} ${cur}` : '—',
                  ])}
                  foot={[isAr ? 'الإجمالي' : 'Total', '', `${data.purchases.length} ${isAr ? 'فاتورة' : 'invoices'}`, `${fmt(s.input_net)} ${cur}`, `${fmt(s.input_vat)} ${cur}`]}
                  language={language}
                />
              ) : (
                <p className="text-sm text-muted-foreground py-4">{t('noDataForPeriod')}</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// REPORT — OTHER EXPENSES (cash book detail)
// ══════════════════════════════════════════════════════════════════════════════
const OtherExpensesReport = ({ ledger, t, language }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const cur = t('currency');

  const filtered = useMemo(() =>
    ledger.filter(e =>
      e.credit > 0 &&
      e.date && new Date(e.date).getFullYear() === year &&
      !LEDGER_EXCLUDE.includes(e.transaction_type)
    ).sort((a, b) => new Date(a.date) - new Date(b.date)),
    [ledger, year]
  );

  const total = filtered.reduce((s, e) => s + (e.credit || 0), 0);

  const byType = filtered.reduce((acc, e) => {
    acc[e.transaction_type] = (acc[e.transaction_type] || 0) + (e.credit || 0);
    return acc;
  }, {});

  const rows = filtered.map(e => [
    e.date,
    e.transaction_type,
    e.description || '—',
    `${fmt(e.credit)} ${cur}`,
  ]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <YearFilter value={year} onChange={setYear} t={t} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(byType).sort((a, b) => b[1] - a[1]).map(([type, amt]) => (
          <StatCard key={type} label={type} value={`${fmt(amt)} ${cur}`} color="text-red-600" language={language} />
        ))}
        <StatCard label={language === 'ar' ? 'الإجمالي' : 'Total'} value={`${fmt(total)} ${cur}`} color="text-red-700" language={language} />
      </div>
      {rows.length > 0 ? (
        <ReportTable
          head={[
            language === 'ar' ? 'التاريخ' : 'Date',
            language === 'ar' ? 'النوع' : 'Type',
            language === 'ar' ? 'البيان' : 'Description',
            language === 'ar' ? 'المبلغ' : 'Amount',
          ]}
          rows={rows}
          foot={[language === 'ar' ? 'الإجمالي' : 'Total', '', '', `${fmt(total)} ${cur}`]}
          language={language}
        />
      ) : <p className="text-sm text-muted-foreground py-4">{t('noDataForPeriod')}</p>}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// REPORT — EXPENSE BREAKDOWN (cash book + supplier invoices by category)
// ══════════════════════════════════════════════════════════════════════════════
const ExpenseBreakdownReport = ({ ledger, supplierInvoices, expenseCategories, t, language }) => {
  const now  = new Date();
  const [mode, setMode]   = useState('year');
  const [year, setYear]   = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const cur  = t('currency');
  const isAr = language === 'ar';

  const catMap = useMemo(() => {
    const m = {};
    expenseCategories.forEach(c => { m[c.id] = c; });
    return m;
  }, [expenseCategories]);

  // Collect all categorised amounts from both sources
  const allEntries = useMemo(() => {
    const entries = [];
    const matchDate = (dateStr) => {
      if (!dateStr) return false;
      const d = new Date(dateStr);
      if (d.getFullYear() !== year) return false;
      if (mode === 'month' && d.getMonth() + 1 !== month) return false;
      return true;
    };
    ledger.forEach(e => {
      if (!e.expense_category_id || !e.credit || !matchDate(e.date)) return;
      entries.push({ category_id: e.expense_category_id, amount: e.credit });
    });
    supplierInvoices.forEach(inv => {
      if (!inv.expense_category_id || !inv.total || !matchDate(inv.date)) return;
      entries.push({ category_id: inv.expense_category_id, amount: inv.total });
    });
    return entries;
  }, [ledger, supplierInvoices, year, month, mode]);

  // Group by category
  const grouped = useMemo(() => {
    const m = {};
    allEntries.forEach(({ category_id, amount }) => {
      const cat = catMap[category_id];
      if (!cat) return;
      const key = cat.category; // English key for stable grouping
      const displayCat = isAr && cat.category_ar ? cat.category_ar : cat.category;
      if (!m[key]) m[key] = { total: 0, subcategories: {}, is_capital: cat.is_capital, displayCat };
      m[key].total += amount;
      m[key].is_capital = m[key].is_capital || cat.is_capital;
      const subKey = cat.subcategory || '—';
      const displaySub = cat.subcategory
        ? (isAr && cat.subcategory_ar ? cat.subcategory_ar : cat.subcategory)
        : '—';
      if (!m[key].subcategories[subKey]) m[key].subcategories[subKey] = { total: 0, items: {}, displaySub };
      m[key].subcategories[subKey].total += amount;
      const itemKey = isAr && cat.item_ar ? cat.item_ar : cat.item;
      m[key].subcategories[subKey].items[itemKey] = (m[key].subcategories[subKey].items[itemKey] || 0) + amount;
    });
    return m;
  }, [allEntries, catMap, isAr]);

  const operating = Object.entries(grouped).filter(([, v]) => !v.is_capital).sort((a, b) => b[1].total - a[1].total);
  const capital = Object.entries(grouped).filter(([, v]) => v.is_capital).sort((a, b) => b[1].total - a[1].total);
  const totalOperating = operating.reduce((s, [, v]) => s + v.total, 0);
  const totalCapital = capital.reduce((s, [, v]) => s + v.total, 0);

  const renderGroup = (entries) => entries.map(([key, data]) => (
    <div key={key} className="border border-border rounded-md overflow-hidden">
      <div className="flex justify-between items-center px-4 py-2 bg-muted/40 font-semibold text-sm">
        <span lang={isAr ? 'ar' : 'en'}>{data.displayCat}</span>
        <span className="font-mono-numeric text-destructive">{fmt(data.total)} {cur}</span>
      </div>
      {Object.entries(data.subcategories).map(([subKey, subData]) => (
        <div key={subKey} className="border-t border-border/50">
          {subData.displaySub !== '—' && (
            <div className="flex justify-between items-center px-6 py-1.5 bg-muted/20 text-xs text-muted-foreground">
              <span lang={isAr ? 'ar' : 'en'}>{subData.displaySub}</span>
              <span className="font-mono-numeric">{fmt(subData.total)} {cur}</span>
            </div>
          )}
          {Object.entries(subData.items).sort((a, b) => b[1] - a[1]).map(([item, amt]) => (
            <div key={item} className="flex justify-between items-center px-8 py-1 text-xs">
              <span lang={isAr ? 'ar' : 'en'}>{item}</span>
              <span className="font-mono-numeric">{fmt(amt)} {cur}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  ));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex rounded-md border border-border overflow-hidden">
          {['year', 'month'].map(m => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-3 py-1.5 text-sm transition-colors ${mode === m ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted/50'}`}
              lang={isAr ? 'ar' : 'en'}
            >
              {m === 'year' ? (isAr ? 'سنة' : 'Year') : (isAr ? 'شهر' : 'Month')}
            </button>
          ))}
        </div>
        {mode === 'year'
          ? <YearFilter value={year} onChange={setYear} t={t} />
          : <MonthYearFilter month={month} year={year} onMonth={setMonth} onYear={setYear} language={language} />
        }
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label={isAr ? 'المصروفات التشغيلية' : 'Operating Expenses'} value={`${fmt(totalOperating)} ${cur}`} color="text-red-600" language={language} />
        <StatCard label={isAr ? 'المشتريات الرأسمالية' : 'Capital Purchases'} value={`${fmt(totalCapital)} ${cur}`} color="text-orange-600" language={language} />
        <StatCard label={isAr ? 'الإجمالي' : 'Total'} value={`${fmt(totalOperating + totalCapital)} ${cur}`} color="text-foreground" language={language} />
      </div>

      {operating.length > 0 && (
        <div>
          <SectionTitle language={language}>{isAr ? 'المصروفات التشغيلية' : 'Operating Expenses'}</SectionTitle>
          <div className="space-y-2">{renderGroup(operating)}</div>
        </div>
      )}

      {capital.length > 0 && (
        <div>
          <SectionTitle language={language}>{isAr ? 'المشتريات الرأسمالية' : 'Capital Purchases'}</SectionTitle>
          <div className="space-y-2">{renderGroup(capital)}</div>
        </div>
      )}

      {allEntries.length === 0 && (
        <p className="text-sm text-muted-foreground py-4">{t('noDataForPeriod')}</p>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// REPORT 6 — PRODUCTION
// ══════════════════════════════════════════════════════════════════════════════
const ProductionReport = ({ orders, deliveryNotes, employees, getAuthHeader, t, language }) => {
  const now = new Date();
  const [month, setMonth]         = useState(now.getMonth() + 1);
  const [year, setYear]           = useState(now.getFullYear());
  const [snapshotEmployees, setSnapshotEmployees] = useState(null);
  const [snapshotLoading, setSnapshotLoading]     = useState(false);
  const [detailLine, setDetailLine] = useState(null);
  const isPastMonth = year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1); // null | 'knits' | 'woven'
  const cur  = t('currency');
  const isAr = language === 'ar';
  const months = isAr ? MONTHS_AR : MONTHS;
  const fmtDate = d => { if (!d) return '—'; const [y, m, day] = d.split('-'); return `${day}-${m}-${y}`; };

  useEffect(() => {
    if (month === 0) { setSnapshotEmployees(null); setSnapshotLoading(false); return; }
    setSnapshotEmployees(null);
    setSnapshotLoading(true);
    axios.get(`${API}/payroll/snapshot/${year}/${month}`, { headers: getAuthHeader() })
      .then(r => setSnapshotEmployees(r.data?.employees || null))
      .catch(() => setSnapshotEmployees(null))
      .finally(() => setSnapshotLoading(false));
  }, [month, year]);

  const empData      = snapshotEmployees || employees;
  const usingLive    = !snapshotEmployees && !snapshotLoading;

  const orderLineMap = useMemo(() => {
    const m = {};
    orders.forEach(o => { m[o.id] = o.line || 'knits'; });
    return m;
  }, [orders]);

  const orderTotalQty = useMemo(() => {
    const m = {};
    orders.forEach(o => {
      m[o.id] = (o.variants || []).reduce((s, v) => s + (v.quantity || 0), 0);
    });
    return m;
  }, [orders]);

  const orderDeliveredQty = useMemo(() => {
    const m = {};
    deliveryNotes.forEach(dn => {
      (dn.items || []).forEach(item => {
        if (item.order_id) m[item.order_id] = (m[item.order_id] || 0) + (item.quantity || 0) + (item.ruined_quantity || 0);
      });
    });
    return m;
  }, [deliveryNotes]);

  const monthPrefix = month === 0 ? `${year}` : `${year}-${String(month).padStart(2, '0')}`;
  const periodLabel = month === 0 ? `${year}` : `${months[month - 1]} ${year}`;

  const deliveredDetail = useMemo(() => {
    const result = { knits: {}, woven: {} };
    deliveryNotes.forEach(dn => {
      if (!(dn.delivery_date || '').startsWith(monthPrefix)) return;
      const client = dn.client_name || '—';
      (dn.items || []).forEach(item => {
        const line = orderLineMap[item.order_id];
        if (line !== 'knits' && line !== 'woven') return;
        const qty = item.quantity || 0;
        if (!qty) return;
        if (!result[line][client]) result[line][client] = [];
        result[line][client].push({
          date: dn.delivery_date,
          item_code: item.item_code || '',
          description: item.description || '',
          quantity: qty,
        });
      });
    });
    return result;
  }, [deliveryNotes, monthPrefix, orderLineMap]);

  const { knitsDelivered, wovenDelivered, knitsRuined, wovenRuined, knitsRevenue, wovenRevenue } = useMemo(() => {
    let knits = 0, woven = 0, knitsRuinedCount = 0, wovenRuinedCount = 0, knitsRev = 0, wovenRev = 0;
    deliveryNotes.forEach(dn => {
      if (!(dn.delivery_date || '').startsWith(monthPrefix)) return;
      (dn.items || []).forEach(item => {
        const line = orderLineMap[item.order_id];
        if (line === 'knits') {
          knits += item.quantity || 0;
          knitsRuinedCount += item.ruined_quantity || 0;
          knitsRev += (item.quantity || 0) * (item.unit_price || 0);
        } else if (line === 'woven') {
          woven += item.quantity || 0;
          wovenRuinedCount += item.ruined_quantity || 0;
          wovenRev += (item.quantity || 0) * (item.unit_price || 0);
        }
      });
    });
    return { knitsDelivered: knits, wovenDelivered: woven, knitsRuined: knitsRuinedCount, wovenRuined: wovenRuinedCount, knitsRevenue: knitsRev, wovenRevenue: wovenRev };
  }, [deliveryNotes, monthPrefix, orderLineMap]);

  const { knitsInFactory, wovenInFactory } = useMemo(() => {
    let knitsIn = 0, wovenIn = 0;
    orders.forEach(o => {
      if (o.status === 'cancelled') return;
      const line = o.line || 'knits';
      if (line !== 'knits' && line !== 'woven') return;
      const total     = orderTotalQty[o.id] || 0;
      const delivered = orderDeliveredQty[o.id] || 0;
      const remaining = Math.max(0, total - delivered);
      if (line === 'knits') knitsIn += remaining;
      else                  wovenIn += remaining;
    });
    return { knitsInFactory: knitsIn, wovenInFactory: wovenIn };
  }, [orders, orderTotalQty, orderDeliveredQty]);

  const { knitsTotalOrdered, wovenTotalOrdered } = useMemo(() => {
    let knitsTotal = 0, wovenTotal = 0;
    orders.forEach(o => {
      if (o.status === 'cancelled') return;
      if (!(o.created_at || '').startsWith(monthPrefix)) return;
      const line = o.line || 'knits';
      if (line !== 'knits' && line !== 'woven') return;
      const total = orderTotalQty[o.id] || 0;
      if (line === 'knits') knitsTotal += total; else wovenTotal += total;
    });
    return { knitsTotalOrdered: knitsTotal, wovenTotalOrdered: wovenTotal };
  }, [orders, orderTotalQty, monthPrefix]);

  const { knitsPool, wovenPool, sharedHalf, knitsTotalCost, wovenTotalCost } = useMemo(() => {
    let knits = 0, woven = 0, shared = 0;
    empData.forEach(e => {
      const np = (e.salary || 0) - (e.ss || 0) + (e.ot || 0) + (e.bonuses || 0) - (e.deductibles || 0);
      if (e.category === 'Knits Line') knits += np;
      else if (e.category === 'Woven Line') woven += np;
      else if (['Administrative', 'Cutting Line', 'Finishing Line'].includes(e.category)) shared += np;
    });
    return { knitsPool: knits, wovenPool: woven, sharedHalf: shared / 2, knitsTotalCost: knits + shared / 2, wovenTotalCost: woven + shared / 2 };
  }, [empData]);

  const knitsOutput   = knitsDelivered + knitsRuined;
  const wovenOutput   = wovenDelivered + wovenRuined;
  const knitsCPP      = knitsOutput > 0 ? knitsTotalCost / knitsOutput : 0;
  const wovenCPP      = wovenOutput > 0 ? wovenTotalCost / wovenOutput : 0;
  const knitsAvgPrice = knitsDelivered > 0 ? knitsRevenue / knitsDelivered : 0;
  const wovenAvgPrice = wovenDelivered > 0 ? wovenRevenue / wovenDelivered : 0;

  const colKnits = 'text-green-600';
  const colWoven = 'text-blue-600';

  return (
    <div className="space-y-6">
      <MonthYearFilter month={month} year={year} onMonth={setMonth} onYear={setYear} language={language} allowFullYear />

      {/* Production Summary */}
      <Card className="p-6 border border-border">
        <SectionTitle language={language}>
          {isAr ? 'ملخص الإنتاج' : 'Production Summary'} — {periodLabel}
        </SectionTitle>
        <div className="grid grid-cols-3 gap-x-4 mt-4">
          <div />
          <div className={`text-center font-semibold ${colKnits} ${isAr ? 'text-base' : 'text-sm'}`} lang={isAr ? 'ar' : 'en'}>
            {isAr ? 'محبكة' : 'Knits'}
          </div>
          <div className={`text-center font-semibold ${colWoven} ${isAr ? 'text-base' : 'text-sm'}`} lang={isAr ? 'ar' : 'en'}>
            {isAr ? 'منسوجات' : 'Woven'}
          </div>
          {[
            { label: isAr ? 'تسليم للعميل' : 'Delivered to Client',         k: knitsDelivered.toLocaleString(),    w: wovenDelivered.toLocaleString(), clickK: () => setDetailLine('knits'), clickW: () => setDetailLine('woven') },
            { label: isAr ? 'ناقص / مشطوب' : 'Incomplete / Written Off',        k: knitsRuined.toLocaleString(),       w: wovenRuined.toLocaleString() },
            { label: isAr ? 'إجمالي الإنتاج' : 'Total Output', bold: true,  k: knitsOutput.toLocaleString(),       w: wovenOutput.toLocaleString() },
            { label: isAr ? 'قطع في المصنع (اليوم)' : 'In Factory (today)', k: knitsInFactory.toLocaleString(),    w: wovenInFactory.toLocaleString() },
            { label: isAr ? 'إجمالي الطلبات المستلمة' : 'Total Ordered',    k: knitsTotalOrdered.toLocaleString(), w: wovenTotalOrdered.toLocaleString() },
          ].map((row, i) => (
            <React.Fragment key={i}>
              <div className={`py-2 border-t border-border/40 ${row.bold ? 'font-semibold' : 'text-muted-foreground'} ${isAr ? 'text-sm' : 'text-xs'}`} lang={isAr ? 'ar' : 'en'}>{row.label}</div>
              <div className={`py-2 border-t border-border/40 text-center font-mono-numeric ${row.bold ? 'font-bold' : 'font-semibold'} ${colKnits} ${row.clickK ? 'cursor-pointer underline decoration-dotted hover:opacity-70' : ''}`} onClick={row.clickK}>{row.k}</div>
              <div className={`py-2 border-t border-border/40 text-center font-mono-numeric ${row.bold ? 'font-bold' : 'font-semibold'} ${colWoven} ${row.clickW ? 'cursor-pointer underline decoration-dotted hover:opacity-70' : ''}`} onClick={row.clickW}>{row.w}</div>
            </React.Fragment>
          ))}
        </div>
      </Card>

      {/* Cost Per Piece */}
      <Card className="p-6 border border-border">
        <SectionTitle language={language}>
          {isAr ? 'تكلفة القطعة' : 'Cost Per Piece'} — {periodLabel}
        </SectionTitle>
        {month === 0 && (
          <p className={`text-amber-600 mb-3 ${isAr ? 'text-sm' : 'text-xs'}`} lang={isAr ? 'ar' : 'en'}>
            {isAr ? 'تكلفة القطعة تُحتسب على أساس شهري — البيانات أدناه تعكس بيانات الموظفين الحالية فقط' : 'Cost per piece is monthly — data below reflects current payroll only'}
          </p>
        )}
        {usingLive && month !== 0 && (
          <p className={`text-muted-foreground mb-3 ${isAr ? 'text-sm' : 'text-xs'}`} lang={isAr ? 'ar' : 'en'}>
            {isPastMonth
              ? (isAr ? 'لا يوجد سجل رواتب محفوظ لهذا الشهر — يتم عرض البيانات الحالية' : 'No payroll snapshot saved for this month — showing current rates')
              : (isAr ? 'بيانات الموظفين الحالية — الشهر لم يُغلق بعد' : 'Using current employee data — month not yet closed')}
          </p>
        )}
        <div className="grid grid-cols-3 gap-x-4 mt-2">
          <div />
          <div className={`text-center font-semibold ${colKnits} ${isAr ? 'text-base' : 'text-sm'}`} lang={isAr ? 'ar' : 'en'}>
            {isAr ? 'محبكة' : 'Knits'}
          </div>
          <div className={`text-center font-semibold ${colWoven} ${isAr ? 'text-base' : 'text-sm'}`} lang={isAr ? 'ar' : 'en'}>
            {isAr ? 'منسوجات' : 'Woven'}
          </div>
          {[
            { label: isAr ? 'رواتب الخط' : 'Line Salary Pool',                                                  k: `${fmt(knitsPool)} ${cur}`,      w: `${fmt(wovenPool)} ${cur}` },
            { label: isAr ? '½ مصاريف مشتركة (إداري + تشطيب + قص)' : '½ Shared (Admin + Finishing + Cutting)', k: `${fmt(sharedHalf)} ${cur}`,     w: `${fmt(sharedHalf)} ${cur}` },
            { label: isAr ? 'إجمالي التكلفة' : 'Total Cost Pool', bold: true,                                   k: `${fmt(knitsTotalCost)} ${cur}`, w: `${fmt(wovenTotalCost)} ${cur}` },
            { label: isAr ? 'إجمالي الإنتاج (تسليم + تالف)' : 'Total Output (Delivered + Ruined)',              k: knitsOutput.toLocaleString(),    w: wovenOutput.toLocaleString() },
            { label: isAr ? 'تكلفة القطعة' : 'Cost Per Piece', bold: true, highlight: true,                    k: `${fmt(knitsCPP)} ${cur}`,       w: `${fmt(wovenCPP)} ${cur}` },
          ].map((row, i) => (
            <React.Fragment key={i}>
              <div className={`py-2 border-t border-border/40 ${row.bold ? 'font-semibold' : 'text-muted-foreground'} ${isAr ? 'text-sm' : 'text-xs'}`} lang={isAr ? 'ar' : 'en'}>{row.label}</div>
              <div className={`py-2 border-t border-border/40 text-center font-mono-numeric ${row.bold ? 'font-bold' : ''} ${row.highlight ? `${colKnits} text-base` : ''}`}>{row.k}</div>
              <div className={`py-2 border-t border-border/40 text-center font-mono-numeric ${row.bold ? 'font-bold' : ''} ${row.highlight ? `${colWoven} text-base` : ''}`}>{row.w}</div>
            </React.Fragment>
          ))}
        </div>
      </Card>

      {/* Average Sales Price */}
      <Card className="p-6 border border-border">
        <SectionTitle language={language}>
          {isAr ? 'متوسط سعر البيع (بدون ضريبة)' : 'Avg Sales Price (ex-VAT)'} — {periodLabel}
        </SectionTitle>
        <div className="grid grid-cols-3 gap-x-4 mt-2">
          <div />
          <div className={`text-center font-semibold ${colKnits} ${isAr ? 'text-base' : 'text-sm'}`} lang={isAr ? 'ar' : 'en'}>
            {isAr ? 'محبكة' : 'Knits'}
          </div>
          <div className={`text-center font-semibold ${colWoven} ${isAr ? 'text-base' : 'text-sm'}`} lang={isAr ? 'ar' : 'en'}>
            {isAr ? 'منسوجات' : 'Woven'}
          </div>
          {[
            { label: isAr ? 'إجمالي الإيرادات (بدون ضريبة)' : 'Total Revenue (ex-VAT)', k: `${fmt(knitsRevenue)} ${cur}`, w: `${fmt(wovenRevenue)} ${cur}` },
            { label: isAr ? 'قطع مُسلَّمة' : 'Pieces Delivered',                         k: knitsDelivered.toLocaleString(), w: wovenDelivered.toLocaleString() },
            { label: isAr ? 'متوسط سعر القطعة' : 'Avg Price / Piece', bold: true, highlight: true, k: `${fmt(knitsAvgPrice)} ${cur}`, w: `${fmt(wovenAvgPrice)} ${cur}` },
          ].map((row, i) => (
            <React.Fragment key={i}>
              <div className={`py-2 border-t border-border/40 ${row.bold ? 'font-semibold' : 'text-muted-foreground'} ${isAr ? 'text-sm' : 'text-xs'}`} lang={isAr ? 'ar' : 'en'}>{row.label}</div>
              <div className={`py-2 border-t border-border/40 text-center font-mono-numeric ${row.bold ? 'font-bold' : ''} ${row.highlight ? `${colKnits} text-base` : ''}`}>{row.k}</div>
              <div className={`py-2 border-t border-border/40 text-center font-mono-numeric ${row.bold ? 'font-bold' : ''} ${row.highlight ? `${colWoven} text-base` : ''}`}>{row.w}</div>
            </React.Fragment>
          ))}
        </div>
      </Card>

      {/* Delivered Detail Modal */}
      {detailLine && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 overflow-auto py-8 px-4" onClick={() => setDetailLine(null)}>
          <div className="bg-background rounded-lg shadow-xl w-full max-w-2xl" dir={isAr ? 'rtl' : 'ltr'} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className={`font-semibold ${isAr ? 'text-base' : 'text-sm'}`} lang={isAr ? 'ar' : 'en'}>
                {detailLine === 'knits' ? (isAr ? 'محبكة' : 'Knits') : (isAr ? 'منسوجات' : 'Woven')}
                {' — '}{isAr ? 'تسليم للعميل' : 'Delivered to Client'}{' — '}{periodLabel}
              </h2>
              <button onClick={() => setDetailLine(null)} className="text-muted-foreground hover:text-foreground text-lg leading-none px-1">✕</button>
            </div>
            <div className="p-4 space-y-5 max-h-[70vh] overflow-y-auto">
              {Object.keys(deliveredDetail[detailLine]).length === 0 && (
                <p className={`text-center text-muted-foreground py-4 ${isAr ? 'text-sm' : 'text-xs'}`} lang={isAr ? 'ar' : 'en'}>
                  {isAr ? 'لا توجد بيانات للفترة المحددة' : 'No deliveries for this period'}
                </p>
              )}
              {Object.entries(deliveredDetail[detailLine])
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([client, items]) => {
                  const clientTotal = items.reduce((s, it) => s + it.quantity, 0);
                  return (
                    <div key={client}>
                      <div className="flex items-center justify-between mb-1">
                        <h3 className={`font-semibold text-primary ${isAr ? 'text-sm' : 'text-xs'}`} lang={isAr ? 'ar' : 'en'}>{client}</h3>
                        <span className={`font-mono-numeric font-semibold text-primary ${isAr ? 'text-sm' : 'text-xs'}`}>{clientTotal.toLocaleString()} {isAr ? 'قطعة' : 'pcs'}</span>
                      </div>
                      <table className="w-full">
                        <thead>
                          <tr className={`text-muted-foreground border-b border-border/40 ${isAr ? 'text-xs' : 'text-xs'}`}>
                            <th className={`font-normal py-1 ${isAr ? 'text-right pl-3' : 'text-left pr-3'}`} lang={isAr ? 'ar' : 'en'}>{isAr ? 'التاريخ' : 'Date'}</th>
                            <th className={`font-normal py-1 ${isAr ? 'text-right pl-3' : 'text-left pr-3'}`}>{isAr ? 'الكود' : 'Code'}</th>
                            <th className={`font-normal py-1 ${isAr ? 'text-right pl-3' : 'text-left pr-3'}`} lang={isAr ? 'ar' : 'en'}>{isAr ? 'الصنف' : 'Item'}</th>
                            <th className={`font-normal py-1 ${isAr ? 'text-left' : 'text-right'}`} lang={isAr ? 'ar' : 'en'}>{isAr ? 'الكمية' : 'Qty'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item, idx) => (
                            <tr key={idx} className="border-b border-border/20 text-xs">
                              <td className={`py-1 font-mono-numeric whitespace-nowrap ${isAr ? 'pl-3' : 'pr-3'}`}>{fmtDate(item.date)}</td>
                              <td className={`py-1 font-mono-numeric ${isAr ? 'pl-3' : 'pr-3'}`}>{item.item_code || '—'}</td>
                              <td className={`py-1 text-muted-foreground ${isAr ? 'pl-3' : 'pr-3'}`}>{item.description}</td>
                              <td className={`py-1 font-mono-numeric font-semibold ${isAr ? 'text-left' : 'text-right'}`}>{item.quantity.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN REPORTS PAGE
// ══════════════════════════════════════════════════════════════════════════════
export const Reports = () => {
  const { getAuthHeader, user } = useAuth();
  const { t, language }   = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'revenue';

  const [loading, setLoading]                     = useState(true);
  const [invoices, setInvoices]                   = useState([]);
  const [payments, setPayments]                   = useState([]);
  const [employees, setEmployees]                 = useState([]);
  const [workers, setWorkers]                     = useState([]);
  const [jobs, setJobs]                           = useState([]);
  const [outsourcingPayments, setOutPay]          = useState([]);
  const [ledger, setLedger]                       = useState([]);
  const [supplierInvoices, setSupplierInvoices]   = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [snapshotCosts, setSnapshotCosts]         = useState({});
  const [etaReceived, setEtaReceived]             = useState([]);
  const [orders, setOrders]                       = useState([]);
  const [deliveryNotes, setDeliveryNotes]         = useState([]);

  useEffect(() => {
    const headers = getAuthHeader();
    Promise.all([
      axios.get(`${API}/invoices`,             { headers }),
      axios.get(`${API}/payments`,             { headers }),
      axios.get(`${API}/employees`,            { headers }),
      axios.get(`${API}/outsourcing/workers`,  { headers }),
      axios.get(`${API}/outsourcing/jobs`,     { headers }),
      axios.get(`${API}/outsourcing/payments`, { headers }),
      axios.get(`${API}/ledger`,               { headers }),
      axios.get(`${API}/supplier-invoices`,    { headers }),
      axios.get(`${API}/expense-categories`,      { headers }),
      axios.get(`${API}/payroll/snapshots/costs`, { headers }),
      axios.get(`${API}/eta/received`,            { headers }),
      axios.get(`${API}/orders`,                  { headers }),
      axios.get(`${API}/delivery-notes`,          { headers }),
    ]).then(([inv, pay, emp, wrk, jbs, opp, led, sinv, cats, snapCosts, eta, ord, dn]) => {
      setInvoices(inv.data           || []);
      setPayments(pay.data           || []);
      setEmployees(emp.data          || []);
      setWorkers(wrk.data            || []);
      setJobs(jbs.data               || []);
      setOutPay(opp.data             || []);
      setLedger(led.data             || []);
      setSupplierInvoices(sinv.data  || []);
      setExpenseCategories(cats.data || []);
      setEtaReceived(eta.data        || []);
      setOrders(ord.data             || []);
      setDeliveryNotes(dn.data       || []);
      const costsMap = {};
      (snapCosts.data || []).forEach(s => { costsMap[`${s.year}-${s.month}`] = s.cost; });
      setSnapshotCosts(costsMap);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px] text-muted-foreground">
        <span lang={language === 'ar' ? 'ar' : 'en'}>{t('loading')}</span>
      </div>
    );
  }

  const commonProps = { t, language };

  return (
    <div className="p-4 md:p-8" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <h1 className="text-2xl md:text-4xl font-bold tracking-tight mb-6 md:mb-8" lang={language === 'ar' ? 'ar' : 'en'}>
        {t('reports')}
      </h1>

      <Tabs value={tab} onValueChange={v => setSearchParams({ tab: v })}>
        <TabsList className="mb-6 flex-wrap h-auto gap-1" dir={language === 'ar' ? 'rtl' : 'ltr'}>
          <TabsTrigger value="revenue"           lang={language === 'ar' ? 'ar' : 'en'}>{t('revenueReport')}</TabsTrigger>
          <TabsTrigger value="pl"                lang={language === 'ar' ? 'ar' : 'en'}>{t('plSummary')}</TabsTrigger>
          <TabsTrigger value="outsourcing"       lang={language === 'ar' ? 'ar' : 'en'}>{t('outsourcingReport')}</TabsTrigger>
          <TabsTrigger value="expense-breakdown" lang={language === 'ar' ? 'ar' : 'en'}>{language === 'ar' ? 'تفصيل المصروفات' : 'Expense Breakdown'}</TabsTrigger>
          <TabsTrigger value="vat-report">VAT Report</TabsTrigger>
          <TabsTrigger value="production" lang={language === 'ar' ? 'ar' : 'en'}>{language === 'ar' ? 'الإنتاج' : 'Production'}</TabsTrigger>
        </TabsList>

        <TabsContent value="revenue">
          <RevenueReport invoices={invoices} payments={payments} {...commonProps} />
        </TabsContent>

        <TabsContent value="pl">
          <PLReport invoices={invoices} etaReceived={etaReceived} employees={employees} outsourcingPayments={outsourcingPayments} ledger={ledger} snapshotCosts={snapshotCosts} {...commonProps} />
        </TabsContent>

        <TabsContent value="outsourcing">
          <OutsourcingCostReport workers={workers} jobs={jobs} outsourcingPayments={outsourcingPayments} {...commonProps} />
        </TabsContent>

        <TabsContent value="expense-breakdown">
          <ExpenseBreakdownReport ledger={ledger} supplierInvoices={supplierInvoices} expenseCategories={expenseCategories} {...commonProps} />
        </TabsContent>

        <TabsContent value="vat-report">
          <VATReport getAuthHeader={getAuthHeader} {...commonProps} />
        </TabsContent>

        <TabsContent value="production">
          <ProductionReport orders={orders} deliveryNotes={deliveryNotes} employees={employees} getAuthHeader={getAuthHeader} {...commonProps} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
